import { ButtonGeneral, Dropdown, RadioButtonList, typographyRem } from '@by-miles/bymiles-styles'
import React, { useContext, useEffect, useState } from 'react'

import { genericErrorCopy, numbersToLimitOptions } from '../../../../../constants'
import { ProviderContext } from '../../../../../Provider'
import { AccessibleFormWrapper } from '../../../../AccessibleFormWrapper'
import { GenericOverlay } from '../../../../Overlays'
import { handleDropdownState } from '../../../utils/handleDropdownState'
import { handlePrefilledDropdown } from '../../../utils/handlePrefilledDropdown'
import { IntroductoryNcdModalStyle } from './IntroductoryNcdModalStyle'
import { radioSectionExtra, radioSections } from './utils/radioSections'
const { h2: H2, h5: H5, paragraphBody: ParagraphBody } = typographyRem

interface IntroductoryNcdModalProps {
  onClick: (selected: string, claimFreeYears: number | null) => void
  ncdModalOption: string
  ncdModalYears: number | null
}

type ErrorProps = {
  radio: string
  dropdown: string
}
export const IntroductoryNcdModal = ({ onClick, ncdModalOption, ncdModalYears }: IntroductoryNcdModalProps) => {
  const {
    handleCloseModal,
    handleShowModal,
    state: { activeModal, showModal, isMobile },
  } = useContext(ProviderContext)

  const [claimFreeYears, setClaimFreeYears] = useState<number | null>(ncdModalYears || null)
  const [selected, setSelected] = useState<string>(ncdModalOption || '')
  const [errors, setErrors] = useState<ErrorProps>({
    radio: '',
    dropdown: '',
  })

  useEffect(() => {
    setClaimFreeYears(ncdModalYears)
  }, [ncdModalYears])

  useEffect(() => {
    setSelected(ncdModalOption)
  }, [ncdModalOption])

  const handleAddDetails = () => {
    if (!selected || selected === 'N/A' || (typeof claimFreeYears !== 'number' && selected !== 'X')) {
      setErrors({
        radio: !selected || selected === 'N/A' ? genericErrorCopy : '',
        dropdown: typeof claimFreeYears !== 'number' ? genericErrorCopy : '',
      })
    } else {
      setErrors({
        radio: '',
        dropdown: '',
      })
      onClick(selected, claimFreeYears)
      handleCloseModal('introductory-ncd-modal-step-2')
    }
  }

  const handleRadioButtonClick = (value: string) => {
    value === 'X' && setClaimFreeYears(null)
    setSelected(value)
  }

  return (
    <IntroductoryNcdModalStyle>
      {showModal && activeModal === 'introductory-ncd-modal-step-1' && (
        <GenericOverlay
          onClose={() => {}}
          hideDismissIcon
          overlay={
            <>
              <H2>We might be able to give you an introductory No Claims Discount.</H2>
              <div className="modal-info-container">
                <ParagraphBody className="modal-info">
                  Please answer a couple more questions, and if you meet our criteria, we'll add an introductory NCD to
                  your policy with us - and it'll be protected as standard.
                </ParagraphBody>
              </div>
              <div>
                <ButtonGeneral
                  label="Continue"
                  onClick={() => {
                    handleCloseModal('introductory-ncd-modal-step-1')
                    handleShowModal('introductory-ncd-modal-step-2')
                  }}
                  dataCy="continue-ncd-modal"
                  className="modal-button"
                  iconRight="IconCaretRight"
                />
              </div>
            </>
          }
        />
      )}
      <AccessibleFormWrapper onSubmit={handleAddDetails}>
        {showModal && activeModal === 'introductory-ncd-modal-step-2' && (
          <GenericOverlay
            onClose={() => {}}
            hideDismissIcon
            overlay={
              <>
                <H2>Your driving.</H2>
                <div className="modal-info-container">
                  <div className="question-text-container">
                    <H5>Which of these applies to you?</H5>
                    <ParagraphBody className="question-info">Please select the most relevant option.</ParagraphBody>
                    <div className="radio-wrapper">
                      <RadioButtonList
                        preFilled={selected || undefined}
                        sections={
                          !selected || selected === 'X' || selected === 'N/A'
                            ? [...radioSections, radioSectionExtra]
                            : radioSections
                        }
                        error=""
                        onClick={(value: string) => handleRadioButtonClick(value)}
                      />
                    </div>
                  </div>
                  {selected && selected !== 'X' && selected !== 'N/A' && (
                    <div className="question-text-container">
                      <H5>How many continuously claim-free years have you had on this other policy?</H5>
                      <ParagraphBody className="question-info">
                        We may ask for proof of this at a later stage.
                      </ParagraphBody>
                      <div className="dropdown-wrapper">
                        <Dropdown
                          handleDropdownChange={option => {
                            setErrors({ ...errors, dropdown: '' })
                            setClaimFreeYears(option?.value)
                          }}
                          isMobile={isMobile}
                          placeholder="Select"
                          options={numbersToLimitOptions(21)}
                          className="dropdown"
                          errorMessage={errors.dropdown}
                          states={handleDropdownState(claimFreeYears, errors.dropdown)}
                          dataCy="title-dropdown-list"
                          preFilled={handlePrefilledDropdown(numbersToLimitOptions(21), claimFreeYears)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <ButtonGeneral
                    type="button"
                    label="Add details"
                    onClick={() => handleAddDetails()}
                    dataCy="add-ncd-modal-details"
                    className="modal-button"
                    iconRight="IconCaretRight"
                  />
                </div>
              </>
            }
          />
        )}
      </AccessibleFormWrapper>
    </IntroductoryNcdModalStyle>
  )
}
